import * as THREE from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { CanonicalEntity, UniversalBundle } from '@/shared/api/types';
import { unrecognizedSourceFurnitureDomain } from './furnitureVisibility';
import { registerNativePartCarrier } from './nativePartOwnership';

export type NativeGeometryContract = {
  kind: 'glb';
  unitScale: number;
  sourceUpAxis: 'y' | 'z';
  partCount: number;
  bindingAuthority?: 'node_name_and_source_index';
  /** sha256 опубликованного GLB-артефакта: версионный параметр GLB-URL,
   * по которому бэкенд включает immutable-кэширование. */
  artifactSha256?: string;
};

export type NativeGeometrySourcePolicy = 'legacy' | 'pro100_exact' | 'sketchup_exact' | 'invalid';

const MAX_NATIVE_PART_COUNT = 1_000_000;

export type PreparedNativeGlbScene = {
  root: THREE.Group;
  index: Map<string, THREE.Object3D>;
  matchedMeshes: number;
  matchedParts: number;
  unmatchedMeshes: number;
};

export type NativeSourceNodeBinding = {
  name: string;
  sourceIndex: number;
  node: THREE.Object3D;
};

/** Decoder sourceIndex is the JSON mesh-bearing node rank, independent of
 * selected scene traversal and the number of material primitives. Cached
 * source node objects retain identity through GLTFLoader name sanitization. */
export async function nativeSourceNodeBindings(gltf: GLTF): Promise<ReadonlyMap<THREE.Object3D, NativeSourceNodeBinding>> {
  const nodes = await gltf.parser.getDependencies('node') as THREE.Object3D[];
  const rawNodes = gltf.parser.json.nodes ?? [];
  const declarations = new Map<THREE.Object3D, NativeSourceNodeBinding | null>();
  let sourceIndex = 0;
  nodes.forEach((node, index) => {
    const raw = rawNodes[index];
    if (raw?.mesh === undefined) return;
    const rank = sourceIndex++;
    declarations.set(node, typeof raw.name === 'string' && raw.name.length
      ? { name: raw.name, sourceIndex: rank, node } : null);
  });
  const bindings = new Map<THREE.Object3D, NativeSourceNodeBinding>();
  gltf.scene.traverse(object => {
    if (!(object as THREE.Mesh).isMesh) return;
    let owner: THREE.Object3D | null = object;
    while (owner) {
      if (declarations.has(owner)) {
        const binding = declarations.get(owner);
        if (binding) bindings.set(object, binding);
        return;
      }
      owner = owner.parent;
    }
  });
  return bindings;
}

/** Isolate canonical part ownership without changing the indexed source
 * node's local pose, world origin or axes. Each parent carrier keeps the exact
 * original parent affine matrix; snapshot all parents before any detach. */
function independentNativePartOwners(
  root: THREE.Object3D,
  bindings: ReadonlyMap<THREE.Object3D, NativeSourceNodeBinding>,
): void {
  root.updateWorldMatrix(true, true);
  const inverse = root.matrixWorld.clone().invert();
  const parents = new Map<THREE.Object3D, THREE.Matrix4>();
  for (const { node } of bindings.values()) {
    if (!parents.has(node)) parents.set(node,
      inverse.clone().multiply(node.parent?.matrixWorld ?? new THREE.Matrix4()));
  }
  for (const [node, matrix] of parents) {
    const carrier = new THREE.Group();
    carrier.name = `native-part-parent:${node.name}`;
    carrier.matrix.copy(matrix);
    carrier.matrixAutoUpdate = false;
    root.add(carrier);
    carrier.add(node);
    registerNativePartCarrier(node, carrier);
  }
}

function recordFrom(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function nativeGeometryContract(bundle: UniversalBundle): NativeGeometryContract | null {
  const source = bundle as UniversalBundle & {
    sourceAnalysis?: { nativeGeometry?: unknown };
    canonicalScene?: { scene?: { viewerHints?: { nativeGeometry?: unknown } } };
  };
  const policy = nativeGeometrySourcePolicy(bundle);
  const exact = policy === 'pro100_exact' || policy === 'sketchup_exact';
  if (policy === 'invalid') return null;
  // Exact decoder publication owners bind through the canonical scene. Legacy source
  // analysis is intentionally not a recovery path for a missing/corrupt
  // canonical contract because it can reintroduce arbitrary-order binding.
  const raw = recordFrom(exact
    ? source.canonicalScene?.scene?.viewerHints?.nativeGeometry
    : source.canonicalScene?.scene?.viewerHints?.nativeGeometry
      ?? source.sourceAnalysis?.nativeGeometry);
  const kind = exact ? raw.kind : String(raw.kind || '').toLowerCase();
  if (kind !== 'glb') return null;
  const unitScale = raw.unitScale;
  const partCount = raw.partCount;
  const sourceUpAxis = exact
    ? raw.sourceUpAxis
    : String(raw.sourceUpAxis || '').toLowerCase();
  if (typeof unitScale !== 'number' || !Number.isFinite(unitScale) || unitScale <= 0) return null;
  if (
    typeof partCount !== 'number'
    || !Number.isSafeInteger(partCount)
    || partCount < 0
    || partCount > MAX_NATIVE_PART_COUNT
  ) return null;
  if (sourceUpAxis !== 'y' && sourceUpAxis !== 'z') return null;
  if (exact && raw.bindingAuthority !== 'node_name_and_source_index') {
    return null;
  }
  return {
    kind: 'glb',
    unitScale,
    sourceUpAxis,
    partCount,
    ...(typeof raw.artifactSha256 === 'string' && /^[0-9a-f]{64}$/.test(raw.artifactSha256)
      ? { artifactSha256: raw.artifactSha256 }
      : {}),
    ...(raw.bindingAuthority === 'node_name_and_source_index'
      ? { bindingAuthority: 'node_name_and_source_index' as const }
      : {}),
  };
}

export function requiresExactNativeGeometry(bundle: UniversalBundle): boolean {
  return ['pro100_exact', 'sketchup_exact'].includes(nativeGeometrySourcePolicy(bundle));
}

export function nativeGeometrySourcePolicy(bundle: UniversalBundle): NativeGeometrySourcePolicy {
  const source = bundle as UniversalBundle & { sourceKind?: unknown };
  const raw = source.sourceKind;
  if (raw === 'pro100_decode') return 'pro100_exact';
  if (raw === 'sketchup_decode') return 'sketchup_exact';
  if (raw === undefined || raw === null) return 'legacy';
  if (typeof raw !== 'string' || !raw) return 'invalid';
  // A near-decoder spelling is a malformed claim, never a legacy bundle.
  const normalized = raw.trim().toLowerCase();
  if (normalized.includes('pro100') || normalized.includes('sketchup')) return 'invalid';
  return 'legacy';
}

function entityDomain(entity: CanonicalEntity): Record<string, unknown> {
  return recordFrom((entity as CanonicalEntity & { domain?: unknown }).domain);
}

export function nativeSupplementalEntities(bundle: UniversalBundle): readonly CanonicalEntity[] {
  const entities = bundle.canonicalScene?.entities ?? [];
  const replacementModuleIds = new Set(entities.filter((entity) => (
    entityDomain(entity).materializedBy === 'pro100.typed-catalog-replacement.v1'
  )).map((entity) => entity.id));
  return entities.filter((entity) => (
    entityDomain(entity).graph3Generated === true
    || entity.id === 'pro100-import:root'
    || replacementModuleIds.has(entity.id)
    || replacementModuleIds.has(String(entity.moduleId || ''))
  ));
}

function attachObjectContract(object: THREE.Object3D, entity: CanonicalEntity, moduleId: string) {
  object.userData.entityId = entity.id;
  object.userData.name = entity.name;
  object.userData.type = entity.kind;
  object.userData.render = entity.render;
  object.userData.domain = (entity as CanonicalEntity & { domain?: unknown }).domain;
  object.userData.moduleId = moduleId;
}

function normalizeNativeMaterialNames(mesh: THREE.Mesh) {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!material) continue;
    if (!material.userData.nativeAlphaState) {
      material.userData.nativeAlphaState = {
        opacity: material.opacity, transparent: material.transparent,
        alphaTest: material.alphaTest, depthWrite: material.depthWrite,
      };
    }
    const original = String(material.name || '');
    const shaderSafe = Array.from(original, (character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      const isControl = codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
      return isControl || codePoint === 0x2028 || codePoint === 0x2029 ? ' ' : character;
    }).join('')
      .replace(/\s+/g, ' ')
      .trim();
    if (shaderSafe === original) continue;
    material.userData.nativeMaterialName = original;
    material.name = shaderSafe || 'Imported material';
  }
}

/**
 * SketchUp печёт односторонние грани: обратная сторона створки/панели
 * остаётся неокрашенной (бежевый backface) и в приоткрытой анимации верхних
 * антресольных фасадов читается как дыра. Публикация SKP рендерится с
 * двусторонними материалами — все материалы привязанных мешей.
 */
function applySketchupDoubleSidedMaterials(mesh: THREE.Mesh) {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!material || !(material as THREE.Material).isMaterial) continue;
    if (material.side === THREE.DoubleSide) continue;
    material.side = THREE.DoubleSide;
    material.needsUpdate = true;
  }
}

function attachBasisEdges(mesh: THREE.Mesh, entity: CanonicalEntity, moduleId: string) {
  const position = mesh.geometry.getAttribute('position');
  if (!position || position.count < 3) return;
  const geometry = new THREE.EdgesGeometry(mesh.geometry, 18);
  const material = new THREE.LineBasicMaterial({
    color: 0x15181d,
    transparent: true,
    opacity: 0.76,
    depthTest: true,
    depthWrite: false,
    toneMapped: false,
  });
  const lines = new THREE.LineSegments(geometry, material);
  lines.name = `${entity.id}::basis_edges`;
  lines.renderOrder = (mesh.renderOrder || 0) + 2;
  lines.userData.basisEdgeOverlay = true;
  lines.userData.edgeHardware = false;
  attachObjectContract(lines, entity, moduleId);
  mesh.add(lines);
}

/**
 * Публикационный контракт видимости. Бэкенд помечает нераспознанную исходную
 * фурнитуру (меш с фурнитурного слоя без принятой детали и без роли ведомого
 * в motion-контракте) каноническим флагом `render.visibleDefault = false`;
 * тот же флаг читает канонический конструктор (`canonicalGeometryOmission`).
 */
export function nativeEntityDrawn(entity: CanonicalEntity): boolean {
  const render = entity.render as { visibleDefault?: unknown } | undefined;
  return render?.visibleDefault !== false;
}

/** Роли фасадного фронта: копланарная пара «фронт / тело» отодвигает тело. */
const FRONT_SURFACE_ROLES = new Set([
  'facade', 'drawer_front', 'false_facade', 'appliance', 'glass_facade', 'glass_panel',
]);

/**
 * Порог копланарности в МИЛЛИМЕТРАХ источника. Точность 24-битного буфера
 * глубины при near=1мм и дистанции кухни 3–6 м — порядка 0.5мм, поэтому
 * совпадающие грани внутри этого зазора мерцают (z-fighting): печь/духовка в
 * нише и плоскость фасадной рамки лежат в одном слое.
 */
export const COPLANAR_EPSILON_MM = 0.6;
/** Доля перекрытия по двум осям, ниже которой пара не считается наложением. */
const COPLANAR_OVERLAP_SHARE = 0.3;

type PolygonOffsetPin = { factor: number; units: number };

function pinMaterialPolygonOffset(
  mesh: THREE.Mesh,
  pin: PolygonOffsetPin,
  sharedMaterials: ReadonlySet<THREE.Material>,
): void {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!material) continue;
    const existing = material.userData.polygonOffsetPin as PolygonOffsetPin | undefined;
    if (existing && existing.factor === pin.factor && existing.units === pin.units) continue;
    // Материал с чужим пином или общий с другими мешами нельзя править
    // делёвкой — клонируем, чтобы смещение глубины осталось точечным.
    if (existing || sharedMaterials.has(material)) {
      const clone = material.clone();
      clone.userData = { ...material.userData, polygonOffsetPin: { ...pin } };
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map((item) => (item === material ? clone : item));
      } else {
        mesh.material = clone;
      }
      continue;
    }
    material.userData.polygonOffsetPin = { ...pin };
  }
}

function materialList(mesh: THREE.Mesh): Array<THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial> {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return materials.filter(Boolean) as Array<THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial>;
}

/**
 * Исходная подача материалов SKP/PRO100 GLB: SketchUp рисует обе стороны
 * поверхности одним материалом (в открытых оболочках изнанка иначе просвечивает
 * бежевым дефолтом), а тайлы текстуры зашиты в UV > 1 — сэмплер обязан
 * повторять, и на COS-текстурах фартука/стены нужна анизотропия, иначе под
 * скользящим углом тайл выглядит растянутым.
 */
export function applyNativeSourceMaterialPresentation(mesh: THREE.Mesh): void {
  for (const material of materialList(mesh)) {
    material.side = THREE.DoubleSide;
    const map = (material as THREE.MeshStandardMaterial).map;
    if (map) {
      map.wrapS = THREE.RepeatWrapping;
      map.wrapT = THREE.RepeatWrapping;
      // WebGLTextures зажимает значение до максимума драйвера.
      map.anisotropy = Math.max(map.anisotropy, 8);
      map.needsUpdate = true;
    }
    material.needsUpdate = true;
  }
}

/**
 * Стабилизирует глубину копланарных пар исходных мешей: пара, чьи мировые AABB
 * почти совпадают по одной оси (зазор < COPLANAR_EPSILON_MM) и заметно
 * перекрываются по двум другим, получает пин polygonOffset на «тело» — фронт
 * (фасад/техника) стабильно выигрывает тест глубины, мерцание исчезает. Пин
 * повторно применяется `FurnitureRenderObject.applyMaterialMode`, иначе смена
 * режима рендера его сбрасывает.
 */
export function stabilizeCoplanarPairs(
  bound: ReadonlyArray<{ mesh: THREE.Mesh; role: string; sourceIndex: number }>,
): number {
  const materialUsers = new Map<THREE.Material, number>();
  for (const { mesh } of bound) {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if (!material) continue;
      materialUsers.set(material, (materialUsers.get(material) ?? 0) + 1);
    }
  }
  const sharedMaterials = new Set(
    [...materialUsers].filter(([, users]) => users > 1).map(([material]) => material),
  );
  const boxes = bound.map(({ mesh }) => new THREE.Box3().setFromObject(mesh));
  const pinned = new Set<string>();
  for (let left = 0; left < bound.length; left += 1) {
    for (let right = left + 1; right < bound.length; right += 1) {
      if (bound[left].sourceIndex === bound[right].sourceIndex) continue;
      const first = boxes[left];
      const second = boxes[right];
      if (first.isEmpty() || second.isEmpty()) continue;
      // Тонкая ось — ось МИНИМАЛЬНОГО перекрытия. Копланарная пара: зазор по
      // тонкой оси меньше COPLANAR_EPSILON_MM, а по двум другим осям боксы
      // перекрываются на существенную долю (грани лежат друг на друге).
      let thinAxis = -1;
      let thinOverlap = Number.POSITIVE_INFINITY;
      let lateralOk = true;
      for (let axis = 0; axis < 3; axis += 1) {
        const key = axis === 0 ? 'x' : axis === 1 ? 'y' : 'z';
        const overlap = Math.min(first.max[key], second.max[key])
          - Math.max(first.min[key], second.min[key]);
        if (overlap <= 0) { thinAxis = -2; break; }
        if (overlap < thinOverlap) {
          thinOverlap = overlap;
          thinAxis = axis;
        }
      }
      if (thinAxis < 0) continue;
      if (thinOverlap > COPLANAR_EPSILON_MM) continue;
      for (let axis = 0; axis < 3; axis += 1) {
        if (axis === thinAxis) continue;
        const key = axis === 0 ? 'x' : axis === 1 ? 'y' : 'z';
        const overlap = Math.min(first.max[key], second.max[key])
          - Math.max(first.min[key], second.min[key]);
        const smaller = Math.min(first.max[key] - first.min[key], second.max[key] - second.min[key]);
        if (smaller <= 0 || overlap / smaller < COPLANAR_OVERLAP_SHARE) { lateralOk = false; break; }
      }
      if (!lateralOk) continue;
      const leftFront = FRONT_SURFACE_ROLES.has(bound[left].role);
      const rightFront = FRONT_SURFACE_ROLES.has(bound[right].role);
      let backIndex: number;
      if (leftFront !== rightFront) {
        backIndex = leftFront ? right : left;
      } else {
        backIndex = bound[left].sourceIndex <= bound[right].sourceIndex ? right : left;
      }
      const backMesh = bound[backIndex].mesh;
      pinMaterialPolygonOffset(backMesh, { factor: 1, units: 2 }, sharedMaterials);
      pinned.add(backMesh.uuid);
    }
  }
  return pinned.size;
}

/**
 * Binds a native GLB scene to the canonical imported-mesh entities.
 *
 * The GLB remains the geometry/material source, while identity, selection,
 * visibility and render-mode behaviour come exclusively from canonical domain
 * entities. This keeps native fidelity without creating a second data model.
 */
export class NativeGlbScene {
  private readonly entities: CanonicalEntity[];
  private readonly module: CanonicalEntity;

  constructor(
    private readonly sourceRoot: THREE.Object3D,
    private readonly bundle: UniversalBundle,
    private readonly contract: NativeGeometryContract,
    private readonly sourceBindings?: ReadonlyMap<THREE.Object3D, NativeSourceNodeBinding>,
  ) {
    this.entities = bundle.canonicalScene?.entities ?? [];
    const module = this.entities.find((entity) => entity.kind === 'module');
    if (!module) throw new Error('Native GLB bundle has no canonical root module');
    this.module = module;
  }

  prepare(): PreparedNativeGlbScene {
    const wrapper = new THREE.Group();
    wrapper.name = 'native-glb-root';
    wrapper.scale.setScalar(this.contract.unitScale);
    if (this.contract.sourceUpAxis === 'z') wrapper.rotation.x = -Math.PI / 2;
    wrapper.add(this.sourceRoot);
    attachObjectContract(wrapper, this.module, this.module.id);

    const imported = this.entities
      // Native mesh binding is a geometry contract, independent from the
      // reviewed furniture role/objectKind used by BOM and editor logic.
      .filter((entity) => {
        const domain = entityDomain(entity);
        return typeof domain.nativeNodeName === 'string'
          && domain.nativeNodeName.length > 0
          && typeof domain.nativeSourceIndex === 'number'
          && Number.isSafeInteger(domain.nativeSourceIndex)
          && domain.nativeSourceIndex >= 0
          && domain.nativeSourceIndex < this.contract.partCount;
      })
      .sort((left, right) => {
        const a = entityDomain(left).nativeSourceIndex as number;
        const b = entityDomain(right).nativeSourceIndex as number;
        return a - b;
      });
    if (this.contract.bindingAuthority === 'node_name_and_source_index') {
      const sourceIndices = imported.map((entity) => entityDomain(entity).nativeSourceIndex as number);
      const completeCohort = imported.length === this.contract.partCount
        && sourceIndices.every((value, index) => value === index);
      if (!completeCohort) {
        throw new Error(
          `GLB canonical binding cohort invalid: entities=${imported.length}, expected=${this.contract.partCount}`,
        );
      }
    }
    const byNodeName = new Map<string, CanonicalEntity[]>();
    for (const entity of imported) {
      const nodeName = String(entityDomain(entity).nativeNodeName || '');
      if (!nodeName) continue;
      const queue = byNodeName.get(nodeName) ?? [];
      queue.push(entity);
      byNodeName.set(nodeName, queue);
    }
    const sourceBindings = this.sourceBindings;
    if (sourceBindings) {
      independentNativePartOwners(this.sourceRoot, sourceBindings);
    }
    const available = [...imported];
    const claimed = new Set<string>();
    const sourceOwners = new Map<string, THREE.Object3D>();
    const index = new Map<string, THREE.Object3D>();
    index.set(this.module.id, wrapper);
    const bound: Array<{ mesh: THREE.Mesh; role: string; sourceIndex: number }> = [];
    let matchedMeshes = 0;
    let matchedParts = 0;
    let unmatchedMeshes = 0;
    let sourceMeshIndex = 0;

    this.sourceRoot.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const binding = sourceBindings?.get(mesh);
      const currentSourceIndex = binding?.sourceIndex ?? sourceMeshIndex;
      sourceMeshIndex += 1;
      const sourceName = sourceBindings ? binding?.name : mesh.name;
      const sourceOwner = binding?.node ?? mesh;
      const namedQueue = sourceName === undefined ? [] : byNodeName.get(sourceName) ?? [];
      let entity = this.contract.bindingAuthority === 'node_name_and_source_index'
        ? namedQueue.find((candidate) => (
          (!claimed.has(candidate.id) || sourceOwners.get(candidate.id) === sourceOwner)
          && entityDomain(candidate).nativeSourceIndex === currentSourceIndex
        ))
        : namedQueue.find((candidate) => !claimed.has(candidate.id));
      if (!entity && this.contract.bindingAuthority !== 'node_name_and_source_index') {
        entity = available.find((candidate) => !claimed.has(candidate.id));
      }
      if (!entity) {
        unmatchedMeshes += 1;
        return;
      }
      if (!claimed.has(entity.id)) matchedParts += 1;
      claimed.add(entity.id);
      sourceOwners.set(entity.id, sourceOwner);
      matchedMeshes += 1;
      normalizeNativeMaterialNames(mesh);
      attachObjectContract(sourceOwner, entity, this.module.id);
      attachObjectContract(mesh, entity, this.module.id);
      applyNativeSourceMaterialPresentation(mesh);
      // A governed PRO100 typed replacement keeps the native entity solely to
      // preserve the exact node/index binding cohort. Its real catalog module
      // is rendered as a canonical overlay; showing both would duplicate the
      // cabinet and conceal the positioned hardware of the replacement.
      // Нераспознанная фурнитура исходника исключается дважды: объектным
      // договором видимости публикации (visibilityContract / visibleDefault,
      // см. nativeEntityDrawn) и доменным признаком нераспознанной фурнитуры.
      mesh.visible = entityDomain(entity).activeCatalogReplacement === undefined
        && !unrecognizedSourceFurnitureDomain(entityDomain(entity))
        && nativeEntityDrawn(entity);
      sourceOwner.visible = mesh.visible;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      if (nativeGeometrySourcePolicy(this.bundle) === 'sketchup_exact') {
        applySketchupDoubleSidedMaterials(mesh);
      }
      attachBasisEdges(mesh, entity, this.module.id);
      index.set(entity.id, sourceOwner);
      // Скрытые меши не участвуют в копланарных парах: их материал может быть
      // общим с видимым мешем, и пин сместил бы глубину видимого.
      if (mesh.visible) {
        bound.push({
          mesh,
          role: String(entity.role || '').trim().toLowerCase(),
          sourceIndex: entityDomain(entity).nativeSourceIndex as number,
        });
      }
    });
    wrapper.updateMatrixWorld(true);
    stabilizeCoplanarPairs(bound);
    return { root: wrapper, index, matchedMeshes, matchedParts, unmatchedMeshes };
  }
}
